import { useEffect, useRef, useState } from 'react';
import { openTickerChart } from '../tickerChart';

export interface TrendingStock {
  ticker: string;
  name: string;
  mentions: number;
  upvotes: number;
  rank: number;
  mentionChange24h: number | null;
  price: number | null;
  changePercent: number | null;
}

interface BubbleNode extends TrendingStock {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
}

interface TooltipState {
  node: BubbleNode;
  x: number;
  y: number;
}

const POS_COLOR = { r: 0, g: 255, b: 65 }; // matrix green
const NEG_COLOR = { r: 255, g: 59, b: 59 }; // alert red
const NEU_COLOR = { r: 86, g: 154, b: 104 }; // dim console green (no market data)
const BUBBLE_FONT = "'SF Mono', 'Menlo', 'Consolas', 'Courier New', monospace";

function bubbleColor(changePercent: number | null) {
  if (changePercent === null) return NEU_COLOR;
  return changePercent >= 0 ? POS_COLOR : NEG_COLOR;
}

/** Intensity 0..1 — saturates at ±7% daily move */
function intensity(changePercent: number | null) {
  if (changePercent === null) return 0.25;
  return Math.min(Math.abs(changePercent) / 7, 1);
}

export function BuzzBubbles() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stocks, setStocks] = useState<TrendingStock[] | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  // Fetch trending buzz, refresh every 5 minutes
  useEffect(() => {
    const load = async () => {
      // Bound the request: the backend can be slow on a cold cache, and a
      // hung fetch would leave "Loading buzz..." up forever
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch('/api/trending', { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setStocks(data.stocks || []);
        setUpdatedAt(data.updatedAt || null);
        setError(false);
      } catch (e) {
        console.error('Error fetching trending buzz:', e);
        setError(true);
      } finally {
        clearTimeout(timeout);
      }
    };
    load();
    const interval = setInterval(load, 300000);
    return () => clearInterval(interval);
  }, []);

  // Physics + rendering
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas || !stocks || stocks.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let width = wrap.clientWidth;
    let height = wrap.clientHeight;
    let nodes: BubbleNode[] = [];
    let hovered: BubbleNode | null = null;
    let rafId = 0;

    const sizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const buildNodes = () => {
      const mentions = stocks.map((s) => s.mentions);
      const maxM = Math.sqrt(Math.max(...mentions));
      const minM = Math.sqrt(Math.min(...mentions));
      const span = Math.max(maxM - minM, 1);
      let rMax = Math.max(Math.min(width, height) / 7, 40);
      let rMin = 20;

      nodes = stocks.map((s) => {
        const t = (Math.sqrt(s.mentions) - minM) / span;
        const r = rMin + t * (rMax - rMin);
        return {
          ...s,
          r,
          x: width / 2 + (Math.random() - 0.5) * width * 0.8,
          y: height / 2 + (Math.random() - 0.5) * height * 0.8,
          vx: 0,
          vy: 0,
        };
      });

      // If bubbles would overflow the canvas, scale all radii down
      const totalArea = nodes.reduce((sum, n) => sum + Math.PI * n.r * n.r, 0);
      const budget = 0.55 * width * height;
      if (totalArea > budget) {
        const k = Math.sqrt(budget / totalArea);
        nodes.forEach((n) => (n.r *= k));
      }
    };

    const tick = () => {
      const cx = width / 2;
      const cy = height / 2;

      for (const n of nodes) {
        // gentle pull toward center + tiny drift
        n.vx += (cx - n.x) * 0.0004;
        n.vy += (cy - n.y) * 0.0004;
        if (!reducedMotion) {
          n.vx += (Math.random() - 0.5) * 0.04;
          n.vy += (Math.random() - 0.5) * 0.04;
        }
        n.vx *= 0.97;
        n.vy *= 0.97;
        n.x += n.vx;
        n.y += n.vy;

        // keep inside walls
        if (n.x - n.r < 0) { n.x = n.r; n.vx = Math.abs(n.vx) * 0.5; }
        if (n.x + n.r > width) { n.x = width - n.r; n.vx = -Math.abs(n.vx) * 0.5; }
        if (n.y - n.r < 0) { n.y = n.r; n.vy = Math.abs(n.vy) * 0.5; }
        if (n.y + n.r > height) { n.y = height - n.r; n.vy = -Math.abs(n.vy) * 0.5; }
      }

      // pairwise collision: push overlapping bubbles apart
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 0.001;
          const minDist = a.r + b.r + 2;
          if (dist < minDist) {
            const overlap = (minDist - dist) / 2;
            const nx = dx / dist;
            const ny = dy / dist;
            a.x -= nx * overlap;
            a.y -= ny * overlap;
            b.x += nx * overlap;
            b.y += ny * overlap;
          }
        }
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      for (const n of nodes) {
        const c = bubbleColor(n.changePercent);
        const t = intensity(n.changePercent);
        const isHovered = hovered === n;

        const gradient = ctx.createRadialGradient(n.x, n.y, n.r * 0.3, n.x, n.y, n.r);
        gradient.addColorStop(0, `rgba(${c.r}, ${c.g}, ${c.b}, 0.06)`);
        gradient.addColorStop(1, `rgba(${c.r}, ${c.g}, ${c.b}, ${0.22 + 0.4 * t})`);

        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
        ctx.lineWidth = isHovered ? 3 : 2;
        ctx.strokeStyle = `rgba(${c.r}, ${c.g}, ${c.b}, ${isHovered ? 1 : 0.55 + 0.45 * t})`;
        ctx.stroke();

        // labels
        ctx.textAlign = 'center';
        ctx.fillStyle = '#fff';
        const tickerSize = Math.max(10, n.r * 0.38);
        ctx.font = `700 ${tickerSize}px ${BUBBLE_FONT}`;
        const showChange = n.r >= 24;
        ctx.textBaseline = showChange ? 'alphabetic' : 'middle';
        ctx.fillText(n.ticker, n.x, n.y, n.r * 1.7);

        if (showChange) {
          const changeSize = Math.max(9, n.r * 0.26);
          ctx.font = `600 ${changeSize}px ${BUBBLE_FONT}`;
          ctx.fillStyle =
            n.changePercent === null
              ? 'rgba(255,255,255,0.55)'
              : `rgba(${c.r}, ${c.g}, ${c.b}, 1)`;
          const label =
            n.changePercent === null
              ? `${n.mentions} 🗣`
              : `${n.changePercent >= 0 ? '+' : ''}${n.changePercent.toFixed(1)}%`;
          ctx.fillText(label, n.x, n.y + changeSize * 1.25, n.r * 1.7);
        }
      }
    };

    const loop = () => {
      tick();
      draw();
      rafId = requestAnimationFrame(loop);
    };

    sizeCanvas();
    buildNodes();

    if (reducedMotion) {
      // settle silently, then render one static frame
      for (let i = 0; i < 400; i++) tick();
      draw();
    } else {
      // pre-settle so bubbles don't fly in from random positions
      for (let i = 0; i < 200; i++) tick();
      loop();
    }

    const observer = new ResizeObserver(() => {
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      if (w === width && h === height) return;
      width = w;
      height = h;
      sizeCanvas();
      buildNodes();
      for (let i = 0; i < 200; i++) tick();
      if (reducedMotion) draw();
    });
    observer.observe(wrap);

    const onMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      hovered =
        nodes.find((n) => {
          const dx = n.x - mx;
          const dy = n.y - my;
          return dx * dx + dy * dy <= n.r * n.r;
        }) || null;
      canvas.style.cursor = hovered ? 'pointer' : 'default';
      setTooltip(hovered ? { node: hovered, x: mx, y: my } : null);
      if (reducedMotion) draw();
    };
    const onMouseLeave = () => {
      hovered = null;
      setTooltip(null);
      if (reducedMotion) draw();
    };
    const onClick = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const hit = nodes.find((n) => {
        const dx = n.x - mx;
        const dy = n.y - my;
        return dx * dx + dy * dy <= n.r * n.r;
      });
      if (hit) openTickerChart(hit.ticker);
    };
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseleave', onMouseLeave);
    canvas.addEventListener('click', onClick);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      canvas.removeEventListener('click', onClick);
      // Nodes are rebuilt on refresh — drop any tooltip pointing at old data
      setTooltip(null);
    };
  }, [stocks]);

  if (error && !stocks) {
    return <div className="empty-state">Buzz feed unavailable</div>;
  }
  if (!stocks) {
    return <div className="loading">Loading buzz...</div>;
  }
  if (stocks.length === 0) {
    return <div className="empty-state">No trending stocks right now</div>;
  }

  const tt = tooltip;
  return (
    <div className="buzz-canvas-wrap" ref={wrapRef}>
      <canvas ref={canvasRef} />
      {updatedAt && (
        <div className="buzz-updated" title={new Date(updatedAt).toLocaleString()}>
          as of {new Date(updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          {error && ' · refresh failed, showing last data'}
        </div>
      )}
      {tt && (
        <div
          className="buzz-tooltip"
          style={{
            left: Math.min(tt.x + 16, (wrapRef.current?.clientWidth || 300) - 190),
            top: Math.max(tt.y - 10, 8),
          }}
        >
          <div className="buzz-tooltip-name">{tt.node.name}</div>
          <div className="buzz-tooltip-row">
            <span>Reddit rank</span>
            <span>#{tt.node.rank}</span>
          </div>
          <div className="buzz-tooltip-row">
            <span>Mentions 24h</span>
            <span>
              {tt.node.mentions}
              {tt.node.mentionChange24h !== null &&
                ` (${tt.node.mentionChange24h >= 0 ? '+' : ''}${Math.round(tt.node.mentionChange24h)}%)`}
            </span>
          </div>
          <div className="buzz-tooltip-row">
            <span>Upvotes</span>
            <span>{tt.node.upvotes}</span>
          </div>
          {tt.node.price !== null && (
            <div className="buzz-tooltip-row">
              <span>Last close</span>
              <span>${tt.node.price.toFixed(2)}</span>
            </div>
          )}
          {tt.node.changePercent !== null && (
            <div className="buzz-tooltip-row">
              <span>Day change</span>
              <span
                style={{
                  color: tt.node.changePercent >= 0 ? '#00ff41' : '#ff3b3b',
                }}
              >
                {tt.node.changePercent >= 0 ? '+' : ''}
                {tt.node.changePercent.toFixed(2)}%
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
